import Decimal from "decimal.js";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CurrencyPrimitive,
  TradingAccountRefundBalance,
} from "@/api/cashier";
import { WithdrawRefundAmountInput } from "@/pages/withdrawals/choose-method/withdraw-amount-input/WithdrawRefundAmountInput";
import type {
  AmountsByMethodId,
  CurrencyConversionBySpaAndCode,
} from "@/pages/withdrawals/types";
import {
  getMaxAmount,
  moneyToDecimal,
  moneyToMajorUnits,
} from "@/utils/helpers";
import type { FlattenedRefundEntry } from "./MethodSelector";

type Props = {
  spaId: string;
  entry: FlattenedRefundEntry;
  cardNeedsConversion: boolean;
  cardSelectedCode: string | null;
  tradingAccount: CashierTradingAccount;
  balance: TradingAccountRefundBalance | undefined;
  refundAmounts: AmountsByMethodId;
  maxRefunds: CurrencyConversionBySpaAndCode | null;
  refundNotApplicable: boolean;
  currencies: Record<string, CurrencyPrimitive>;
  isLast: boolean;
  isFirstOfMany: boolean;
  onTradingValueChange?: (key: string, value: Decimal | null) => void;
  onRefundChange: (key: string, amount: Decimal | undefined) => void;
  onError?: (error: string) => void;
  locale: string;
  lastUpdated?: number;
  nextUpdate?: number;
};

export const RefundEntryRow = ({
  spaId,
  entry,
  cardNeedsConversion,
  cardSelectedCode,
  tradingAccount,
  balance,
  refundAmounts,
  maxRefunds,
  refundNotApplicable,
  currencies,
  isLast,
  isFirstOfMany,
  onTradingValueChange,
  onRefundChange,
  onError,
  locale,
  lastUpdated,
  nextUpdate,
}: Props) => {
  const { t } = useTranslation();
  const depositCode = entry.minimum_amount.code;
  const isSameCurrency = depositCode === tradingAccount.currency.code;

  const enteredAmount = refundAmounts[`${spaId}_${depositCode}`];

  const depositConversionBalance = !isSameCurrency
    ? balance?.currency_conversion_by_asset?.[entry.ledger_id]?.[depositCode]
    : undefined;

  // Convert this row's amount deposit currency → trading currency. The rate
  // MUST come from the entry's OWN deposit currency, NOT the card-level
  // selected/receiving currency: on a multi-currency card (e.g. a bank ledger
  // funded by USD + EUR + JPY) each asset converts to the trading currency at
  // its own rate. Reading the selected currency's conversion made every sibling
  // use the same rate, so e.g. "100 USD" and "100 EUR" collapsed to the same
  // trading amount (PNX-5790). Card-level conversion into the selected receiving
  // currency happens afterwards on the summed total (see MethodSelector).
  const tradingValue = useMemo((): Decimal | null => {
    if (isSameCurrency || !enteredAmount) return null;
    if (depositConversionBalance?.rate) {
      const rate =
        depositConversionBalance.rate === "0"
          ? "1"
          : depositConversionBalance.rate;
      return enteredAmount.mul(new Decimal(rate));
    }
    return null;
  }, [isSameCurrency, enteredAmount, depositConversionBalance]);

  useEffect(() => {
    if (!onTradingValueChange) return;
    const key = `${spaId}_${depositCode}`;
    if (isSameCurrency) {
      // Row already in trading currency — report as-is.
      onTradingValueChange(key, enteredAmount ?? null);
      return;
    }
    if (!enteredAmount) {
      onTradingValueChange(key, null);
      return;
    }
    if (cardNeedsConversion && !cardSelectedCode) {
      // Awaiting receiving-currency selection — can't convert yet.
      onTradingValueChange(key, null);
      return;
    }
    onTradingValueChange(key, tradingValue);
  }, [
    spaId,
    depositCode,
    cardNeedsConversion,
    isSameCurrency,
    cardSelectedCode,
    tradingValue,
    // enteredAmount is a Decimal object — compare by string to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    enteredAmount?.toString(),
    onTradingValueChange,
  ]);

  // The per-entry maximum (and its trading-currency equivalent) is fixed by the
  // entry's OWN deposit/asset currency and MUST NOT change when the user selects
  // a card-level receiving/processing currency. `maxRefunds` is already keyed by
  // the entry's deposit currency (and accounts for sibling allocations sharing the
  // same ledger), so use it directly. Previously this read `conversionBalance`,
  // which is looked up by `cardSelectedCode`; on a multi-currency card that made
  // sibling entries (e.g. a JPY entry while USD is selected) display the selected
  // asset's amount under their own currency label (e.g. "500 JPY" instead of
  // "10,000 JPY").
  const max = getMaxAmount(maxRefunds?.[spaId]?.[depositCode]);

  const trading = {
    currency: tradingAccount.currency,
    amount: maxRefunds?.[spaId]?.[depositCode]?.tradingAmount ?? new Decimal(0),
  };

  // Derive "1 <deposit> = X <trading>" rate from the balance max amounts.
  // trading.amount / max gives trading units per 1 deposit unit (e.g. 1 EUR = 1.17 USD).
  // Only relevant for non-same-currency rows where the dual-column input is visible.
  const displayRate =
    !isSameCurrency && max && max.gt(0) && trading.amount.gt(0)
      ? trading.amount.div(max).toNumber()
      : undefined;

  // Disable the input + MAX button when the refund account has no available
  // balance, so amounts can't be entered (incl. a negative value via MAX).
  const accountAvailable = moneyToMajorUnits(
    balance?.ledger_amounts?.[entry.ledger_id]
  );
  const accountHasZeroBalance =
    accountAvailable === null || accountAvailable <= 0;

  return (
    <div
      onPaste={(event) => {
        if (
          (event.target as Element).matches(
            `#refundInput_${spaId}_${depositCode}`
          )
        ) {
          const text = event.clipboardData.getData("text/plain");
          if (text.includes("-") || text.includes("e")) event.preventDefault();
        }
      }}
      onBeforeInput={(event) => {
        if (
          (event.target as Element).matches(
            `#refundInput_${spaId}_${depositCode}`
          )
        ) {
          if (event.data === "-" || event.data === "e") event.preventDefault();
        }
      }}
      key={`${spaId}_${depositCode}`}
    >
      {isFirstOfMany && (
        <div
          style={{
            color: "var(--color-text-tertiary)",
            fontSize: "12px",
            paddingBlockEnd: "16px",
          }}
        >
          {t("withdrawals.methods.multiple_currencies_notice")}
        </div>
      )}
      <WithdrawRefundAmountInput
        disabled={
          refundNotApplicable ||
          accountHasZeroBalance ||
          (cardNeedsConversion && !cardSelectedCode)
        }
        id={`refundInput_${spaId}_${depositCode}`}
        min={
          cardNeedsConversion
            ? new Decimal(0)
            : (moneyToDecimal(entry.minimum_amount) ?? undefined)
        }
        max={max}
        currency={currencies[depositCode]}
        trading={trading}
        tradingValue={
          cardNeedsConversion && !isSameCurrency ? tradingValue : undefined
        }
        onChange={(amount) => onRefundChange(`${spaId}_${depositCode}`, amount)}
        onError={onError}
        style={
          !isLast
            ? {
                borderBottom:
                  "1px solid var(--color-stroke-primary, #03142e1a)",
                marginBottom: "16px",
              }
            : {}
        }
        locale={locale}
        exchangeRate={displayRate}
        exchangeRateUnit={displayRate != null ? 1 : undefined}
        lastUpdated={lastUpdated}
        nextUpdate={nextUpdate}
      />
    </div>
  );
};
